import type { SxProps, Theme } from "@mui/material";
import type {
  DataGridPremiumProps,
  GridApi,
  GridColDef,
  GridGroupingColDefOverride,
  GridRenderCellParams,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useMemo, useRef } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import { useGridGroupsExpanded } from "@entrilia/shared/components/grid/gridHooks";
import type { ClientInfo } from "../../../api/types/clientTypes";
import type { DataGridGroup } from "../../common/grid/gridTypes";
import { GroupHeader, GroupName, NameColumn } from "./clientsTableProvider";

const getNameGroupingColumn = (companies: DataGridGroup<ClientInfo>[]): GridGroupingColDefOverride => {
  return {
    hideDescendantCount: true,
    headerName: "Name",
    sortable: true,
    aggregable: false,
    hideable: false,
    pinnable: false,
    filterable: false,
    flex: 1,
    sortComparator: (_v1, _v2, cellParams1, cellParams2) => {
      const direction = (cellParams1.api as GridApi).getSortModel()?.[0]?.sort ?? "asc";
      if (cellParams1.rowNode.type === "group" && cellParams2.rowNode.type === "group") {
        const firstGroupOrder = companies.find((r) => r.group === cellParams1.value)?.groupOrder || 0;
        const secondGroupOrder = companies.find((r) => r.group === cellParams2.value)?.groupOrder || 0;
        return direction === "asc" ? firstGroupOrder - secondGroupOrder : secondGroupOrder - firstGroupOrder;
      }
      const firstTemplateName = companies.find((c) => c.clientCode === cellParams1.rowNode.id)?.title || "";
      const secondTemplateName = companies.find((c) => c.clientCode === cellParams2.rowNode.id)?.title || "";
      return firstTemplateName.localeCompare(secondTemplateName);
    },
    renderHeader: (params) => <GroupHeader headerName={params.colDef.headerName} />,
    renderCell: (params: GridRenderCellParams<ClientInfo, unknown, unknown>) => {
      if (params.rowNode.type === "group") {
        return <GroupName params={params} />;
      }
      return <NameColumn company={params.row} />;
    },
  };
};

interface Props {
  companies: DataGridGroup<ClientInfo>[];
}

const getRowId = (row: DataGridGroup<ClientInfo>) => row.clientCode;

const columnDefinitions: GridColDef<DataGridGroup<ClientInfo>>[] = [];

const gridSx: SxProps<Theme> = {
  ".MuiDataGrid-columnHeader": {
    pl: 1,
  },
};

const ClientsTableView = ({ companies }: Props) => {
  const sortModelRef = useRef<GridSortModel>([]);
  const apiRef = useGridApiRef();

  const { isGroupExpanded } = useGridGroupsExpanded(apiRef);

  const nameGroupingColumn = useMemo(() => getNameGroupingColumn(companies), [companies]);

  return (
    <DataGrid<DataGridGroup<ClientInfo>>
      apiRef={apiRef}
      treeData
      getTreeDataPath={getTreeDataPath}
      columns={columnDefinitions}
      rows={companies}
      getRowId={getRowId}
      loading={false}
      isGroupExpandedByDefault={isGroupExpanded}
      groupingColDef={nameGroupingColumn}
      disableColumnReorder
      sx={gridSx}
      sortingOrder={["asc", "desc"]}
      onSortModelChange={(model) => (sortModelRef.current = model)}
    />
  );
};

export default ClientsTableView;

const getTreeDataPath: DataGridPremiumProps["getTreeDataPath"] = (row: DataGridGroup<ClientInfo>) => [
  row.group,
  row.clientCode,
];
